/**
 * offlineDb.ts
 * IndexedDB wrapper (v2) for:
 *  - Caching any API GET response generically (apiCache store)
 *  - Individual order / customer / product records
 *  - Offline mutation queue with auth token + conflict metadata
 *  - Detected conflicts pending user resolution
 */

const DB_NAME = 'toola-offline';
const DB_VERSION = 3; // v3: adds meta store
// Maximum age for cached API responses before pruning (7 days)
const MAX_CACHE_AGE_MS = 7 * 24 * 60 * 60 * 1000;

function generateUUID(): string {
    const cryptoObj = globalThis.crypto as (Crypto & { randomUUID?: () => string }) | undefined;

    if (cryptoObj?.randomUUID) {
        return cryptoObj.randomUUID();
    }

    // Fallback RFC4122 v4 for older browsers (e.g. Safari 10, Chrome 51)
    const bytes = new Uint8Array(16);

    if (cryptoObj?.getRandomValues) {
        cryptoObj.getRandomValues(bytes);
    } else {
        for (let i = 0; i < bytes.length; i += 1) {
            bytes[i] = Math.floor(Math.random() * 256);
        }
    }

    bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10

    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export const STORES = {
    ORDERS: 'orders',
    ORDERS_LIST: 'ordersList',
    CUSTOMERS: 'customers',
    PRODUCTS: 'products',
    PENDING_MUTATIONS: 'pendingMutations',
    API_CACHE: 'apiCache',
    CONFLICTS: 'conflicts',
    META: 'meta',
} as const;

export type StoreName = (typeof STORES)[keyof typeof STORES];

// ── Singleton DB connection ───────────────────────────────────────────────────
// Opening IndexedDB on every operation is expensive. We keep a single
// connection alive for the lifetime of the page.
let __dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
    if (__dbPromise) return __dbPromise;
    __dbPromise = new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onupgradeneeded = (event) => {
            const db = (event.target as IDBOpenDBRequest).result;

            if (!db.objectStoreNames.contains(STORES.ORDERS))
                db.createObjectStore(STORES.ORDERS, { keyPath: '_id' });
            if (!db.objectStoreNames.contains(STORES.ORDERS_LIST))
                db.createObjectStore(STORES.ORDERS_LIST, { keyPath: 'cacheKey' });
            if (!db.objectStoreNames.contains(STORES.CUSTOMERS))
                db.createObjectStore(STORES.CUSTOMERS, { keyPath: '_id' });
            if (!db.objectStoreNames.contains(STORES.PRODUCTS))
                db.createObjectStore(STORES.PRODUCTS, { keyPath: '_id' });
            if (!db.objectStoreNames.contains(STORES.PENDING_MUTATIONS))
                db.createObjectStore(STORES.PENDING_MUTATIONS, {
                    keyPath: 'id',
                    autoIncrement: true,
                });
            if (!db.objectStoreNames.contains(STORES.API_CACHE))
                db.createObjectStore(STORES.API_CACHE, { keyPath: 'url' });
            if (!db.objectStoreNames.contains(STORES.CONFLICTS))
                db.createObjectStore(STORES.CONFLICTS, {
                    keyPath: 'id',
                    autoIncrement: true,
                });
            // v3: persistent SW configuration (e.g. API base URL)
            if (!db.objectStoreNames.contains(STORES.META))
                db.createObjectStore(STORES.META, { keyPath: 'key' });
        };

        request.onsuccess = () => {
            const db = request.result;
            // If the browser force-closes the connection (e.g. another tab
            // bumps the version), reset the singleton so it re-opens cleanly
            db.onclose = () => { __dbPromise = null; };
            resolve(db);
        };
        request.onerror = () => {
            __dbPromise = null; // allow retry
            reject(request.error);
        };
    });
    return __dbPromise;
}

/**
 * Prune apiCache entries older than MAX_CACHE_AGE_MS.
 * Call on app start (non-blocking) to prevent unbounded storage growth.
 */
export async function pruneStaleApiCache(): Promise<void> {
    try {
        const db = await openDB();
        const cutoff = Date.now() - MAX_CACHE_AGE_MS;
        const tx = db.transaction(STORES.API_CACHE, 'readwrite');
        const store = tx.objectStore(STORES.API_CACHE);
        const req = store.openCursor();
        req.onsuccess = (e: Event) => {
            const cursor = (e.target as IDBRequest<IDBCursorWithValue>).result;
            if (!cursor) return;
            if ((cursor.value.cachedAt || 0) < cutoff) cursor.delete();
            cursor.continue();
        };
    } catch {
        // Fail silently — pruning is best-effort
    }
}

async function get<T>(storeName: StoreName, key: string | number): Promise<T | null> {
    try {
        const db = await openDB();
        return new Promise((resolve) => {
            const tx = db.transaction(storeName, 'readonly');
            const store = tx.objectStore(storeName);
            const req = store.get(key);
            req.onsuccess = () => resolve(req.result ?? null);
            req.onerror = () => resolve(null);
        });
    } catch {
        return null;
    }
}

async function set(storeName: StoreName, value: any): Promise<void> {
    try {
        const db = await openDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(storeName, 'readwrite');
            const store = tx.objectStore(storeName);
            const req = store.put(value);
            req.onsuccess = () => resolve();
            req.onerror = () => reject(req.error);
        });
    } catch {
        // Fail silently — the offline cache must never block the app
    }
}

async function getAll<T>(storeName: StoreName): Promise<T[]> {
    try {
        const db = await openDB();
        return new Promise((resolve) => {
            const tx = db.transaction(storeName, 'readonly');
            const store = tx.objectStore(storeName);
            const req = store.getAll();
            req.onsuccess = () => resolve(req.result ?? []);
            req.onerror = () => resolve([]);
        });
    } catch {
        return [];
    }
}

async function deleteItem(storeName: StoreName, key: string | number): Promise<void> {
    try {
        const db = await openDB();
        return new Promise((resolve) => {
            const tx = db.transaction(storeName, 'readwrite');
            const store = tx.objectStore(storeName);
            store.delete(key);
            tx.oncomplete = () => resolve();
            tx.onerror = () => resolve();
        });
    } catch {
        // Fail silently
    }
}

export type PendingMutation = {
    id?: number;
    type: 'CREATE_ORDER' | 'UPDATE_ORDER';
    payload: any;
    /** Auth token captured at the time the mutation was queued */
    token: string;
    /**
     * Idempotency key for CREATE mutations. Sent as the `X-Idempotency-Key`
     * header on replay so the server can discard duplicate POSTs. It is kept
     * OUTSIDE `payload` because the backend validates the request body strictly
     * and would reject an unknown `clientId` field with a 400 error.
     */
    clientId?: string;
    /**
     * The `updatedAt` value of the order as it was when the local edit was made.
     * Used to detect server-side conflicts during sync.
     */
    serverUpdatedAt?: string;
    createdAt: number;
};

export type ConflictRecord = {
    id?: number;
    orderId: string;
    /** The local changes the user was trying to save */
    localVersion: any;
    /** The version currently on the server */
    serverVersion: any;
    /** Snapshot of the original mutation for reference */
    mutation: PendingMutation;
    detectedAt: number;
    status: 'pending' | 'resolved';
};

export type ApiCacheEntry = {
    url: string;
    data: any;
    cachedAt: number;
};

export const offlineDb = {
    STORES,

    // ─── Orders ───────────────────────────────────────────────────────────────
    cacheOrder: (order: any) => set(STORES.ORDERS, order),
    getCachedOrder: (id: string) => get<any>(STORES.ORDERS, id),

    cacheOrdersList: (cacheKey: string, data: any) =>
        set(STORES.ORDERS_LIST, { cacheKey, data, cachedAt: Date.now() }),
    getCachedOrdersList: (cacheKey: string) =>
        get<{ cacheKey: string; data: any; cachedAt: number }>(STORES.ORDERS_LIST, cacheKey),

    // ─── Customers ────────────────────────────────────────────────────────────
    cacheCustomers: (customers: any[]) =>
        Promise.all(customers.map((c) => set(STORES.CUSTOMERS, c))),
    getCachedCustomers: () => getAll<any>(STORES.CUSTOMERS),

    // ─── Products ─────────────────────────────────────────────────────────────
    cacheProducts: (products: any[]) =>
        Promise.all(products.map((p) => set(STORES.PRODUCTS, p))),
    getCachedProducts: () => getAll<any>(STORES.PRODUCTS),

    // ─── Generic API cache (any GET endpoint) ─────────────────────────────────
    cacheApiResponse: (url: string, data: any) =>
        set(STORES.API_CACHE, { url, data, cachedAt: Date.now() } as ApiCacheEntry),
    getCachedApiResponse: (url: string) => get<ApiCacheEntry>(STORES.API_CACHE, url),

    // ─── Offline mutation queue ────────────────────────────────────────────────
    addPendingMutation: (mutation: Omit<PendingMutation, 'id' | 'createdAt'>) => {
        // Stable idempotency key for CREATE mutations. It is stored at the TOP
        // LEVEL of the mutation record (and replayed as the X-Idempotency-Key
        // header), NEVER inside the request body: the backend validates the body
        // strictly (Joi) and rejects any unknown `clientId` key with a 400 error.
        const clientId =
            mutation.type === 'CREATE_ORDER'
                ? mutation.clientId || mutation.payload?.clientId || generateUUID()
                : mutation.clientId;
        // Defensive: ensure the body never carries the idempotency key.
        const payload = { ...mutation.payload };
        delete (payload as any).clientId;
        return set(STORES.PENDING_MUTATIONS, {
            ...mutation,
            payload,
            clientId,
            createdAt: Date.now(),
        });
    },
    getPendingMutations: () => getAll<PendingMutation>(STORES.PENDING_MUTATIONS),
    deletePendingMutation: (id: number) => deleteItem(STORES.PENDING_MUTATIONS, id),
    clearAllMutations: async () => {
        const all = await getAll<PendingMutation>(STORES.PENDING_MUTATIONS);
        await Promise.all(all.map((m) => m.id !== undefined && deleteItem(STORES.PENDING_MUTATIONS, m.id as number)));
    },

    // ─── Conflicts ────────────────────────────────────────────────────────────
    addConflict: (conflict: Omit<ConflictRecord, 'id'>) =>
        set(STORES.CONFLICTS, conflict),
    getPendingConflicts: async () => {
        const all = await getAll<ConflictRecord>(STORES.CONFLICTS);
        return all.filter((c) => c.status === 'pending');
    },
    resolveConflict: async (id: number) => {
         const existing = await get<ConflictRecord>(STORES.CONFLICTS, id);
         if (!existing) return;
         return set(STORES.CONFLICTS, { ...existing, status: 'resolved' });
     },
    deleteConflict: (id: number) => deleteItem(STORES.CONFLICTS, id),
};
